/**
 * SCHADS MA000100 cl 25.10 remote work (inserted by PR737905 ppc 1 July 2022).
 *
 * Artifact:
 * docs/operations/clause-verifications/2026-08-15-aged-care-legislation-schads-cl-25-10-remote-work.md
 */
import { DEFAULT_TZ, dateOnlyInTz, getHourInTz } from './timezone';
import { roundMoney, sumMoney } from './money-rounding';
import type { ShiftPayBreakdown, ShiftPayInput, ShiftPayWithOvertimeInput } from './schads';

export const SCHADS_REMOTE_WORK_CLAUSE = 'cl 25.10';

export type SchadsRemoteWorkKind = 'on_call' | 'not_on_call' | 'meeting_or_training';

const SPAN_START_HOUR = 6;
const SPAN_END_HOUR = 20;
const ON_CALL_DAY_END_HOUR = 22;
const QUARTER_HOUR = 0.25;

export function schadsRemoteWorkMinimumHours(
  kind: SchadsRemoteWorkKind,
  startTime: Date,
  timezone: string = DEFAULT_TZ,
): number {
  if (kind === 'not_on_call' || kind === 'meeting_or_training') return 1;
  const hour = getHourInTz(startTime, timezone);
  return hour >= ON_CALL_DAY_END_HOUR || hour < SPAN_START_HOUR ? 0.5 : 0.25;
}

/** cl 25.10(c): pay actual time, at least the minimum, then round up to 15 minutes. */
export function schadsRemoteWorkPayableHours(
  actualHours: number,
  kind: SchadsRemoteWorkKind,
  startTime: Date,
  timezone: string = DEFAULT_TZ,
): number {
  const safeActual = Number.isFinite(actualHours) && actualHours > 0 ? actualHours : 0;
  const minimum = schadsRemoteWorkMinimumHours(kind, startTime, timezone);
  const roundedActual = Math.ceil(safeActual / QUARTER_HOUR - 1e-9) * QUARTER_HOUR;
  return Math.max(minimum, roundedActual);
}

function weekdayName(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: timezone }).format(date);
}

function isOutsideOrdinarySpan(hour: number): boolean {
  return hour < SPAN_START_HOUR || hour >= SPAN_END_HOUR;
}

function remoteMultiplier(args: {
  casual: boolean;
  isPublicHoliday: boolean;
  weekday: string;
  hour: number;
  outsideSpanHoursAlready: number;
}): number {
  if (args.isPublicHoliday) return args.casual ? 2.75 : 2.5;
  if (args.weekday === 'Sun') return args.casual ? 2.25 : 2.0;
  const outside = isOutsideOrdinarySpan(args.hour);
  if (args.weekday === 'Sat') {
    if (outside && args.outsideSpanHoursAlready >= 2) return args.casual ? 2.25 : 2.0;
    return args.casual ? 1.75 : 1.5;
  }
  if (outside) {
    if (args.outsideSpanHoursAlready >= 2) return args.casual ? 2.25 : 2.0;
    return args.casual ? 1.75 : 1.5;
  }
  return args.casual ? 1.25 : 1.0;
}

export function calculateSchadsRemoteWorkPay(args: {
  input: ShiftPayInput | ShiftPayWithOvertimeInput;
  baseHourlyRate: number;
  normalizedLevel: ShiftPayBreakdown['normalizedLevel'];
  warnings: string[];
}): ShiftPayBreakdown {
  const { input, baseHourlyRate, normalizedLevel } = args;
  const warnings = [...args.warnings];
  const tz = input.timezone ?? DEFAULT_TZ;
  const kind: SchadsRemoteWorkKind = input.remoteWorkKind ?? 'not_on_call';
  const rawMs = input.endTime.getTime() - input.startTime.getTime();
  const actualHours = rawMs > 0 ? rawMs / (1000 * 60 * 60) : 0;
  const payableHours = schadsRemoteWorkPayableHours(actualHours, kind, input.startTime, tz);

  const casual = input.employmentType === 'casual';
  const otInput = input as ShiftPayWithOvertimeInput;
  const priorWeek = Math.max(0, otInput.ordinaryHoursWorkedEarlierThisWeek ?? 0);
  const weeklyLimit = otInput.ordinaryHoursLimitPerWeek ?? 38;
  const dailyLimit = otInput.ordinaryHoursLimitPerShift ?? 10;

  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  };

  let basePay = 0;
  let penaltyPay = 0;
  let overtimePay = 0;
  let overtimeHours = 0;
  let ordinaryHoursThisShift = 0;
  let outsideSpanHours = 0;
  let cursor = input.startTime.getTime();
  let remaining = payableHours;

  while (remaining > 1e-9) {
    const chunk = Math.min(QUARTER_HOUR, remaining);
    const at = new Date(cursor);
    const hour = getHourInTz(at, tz);
    const weekday = weekdayName(at, tz);
    const isPublicHoliday = Boolean(input.isPublicHoliday);
    const weeklyHeadroom = Math.max(0, weeklyLimit - priorWeek - ordinaryHoursThisShift);
    const dailyHeadroom = Math.max(0, dailyLimit - ordinaryHoursThisShift);
    const weeklyConstrained = otInput.ordinaryHoursWorkedEarlierThisWeek !== undefined;
    const ordinaryHeadroom = weeklyConstrained
      ? Math.min(dailyHeadroom, weeklyHeadroom)
      : dailyHeadroom;

    const treatAsDailyOrWeeklyOt = ordinaryHeadroom <= 0;
    // cl 25.10(d)(i)(C): first two *overtime* hours at 150%/175%, then 200%/225%.
    const overtimeHoursAlready = overtimeHours;
    const multiplier = treatAsDailyOrWeeklyOt
      ? casual
        ? weekday === 'Sun' || isPublicHoliday
          ? isPublicHoliday
            ? 2.75
            : 2.25
          : overtimeHoursAlready >= 2
            ? 2.25
            : 1.75
        : isPublicHoliday
          ? 2.5
          : weekday === 'Sun'
            ? 2.0
            : overtimeHoursAlready >= 2
              ? 2.0
              : 1.5
      : remoteMultiplier({
          casual,
          isPublicHoliday,
          weekday,
          hour,
          outsideSpanHoursAlready: outsideSpanHours,
        });

    // eslint-disable-next-line statura/no-floating-point-money -- SCHADS cl 25.10 remote-work rate, validated against FWO G01002983
    const chunkPay = chunk * baseHourlyRate * multiplier;
    // eslint-disable-next-line statura/no-floating-point-money -- SCHADS cl 25.10 remote-work rate, validated against FWO G01002983
    const ordinaryEquivalent = chunk * baseHourlyRate * (casual ? 1.25 : 1.0);

    if (treatAsDailyOrWeeklyOt) {
      overtimeHours += chunk;
      overtimePay += chunkPay;
    } else {
      ordinaryHoursThisShift += chunk;
      if (isPublicHoliday) {
        bands.publicHoliday += chunk;
        basePay += ordinaryEquivalent;
        penaltyPay += chunkPay - ordinaryEquivalent;
      } else if (weekday === 'Sun') {
        bands.sundayOrdinary += chunk;
        basePay += ordinaryEquivalent;
        penaltyPay += chunkPay - ordinaryEquivalent;
      } else if (weekday === 'Sat') {
        bands.saturdayOrdinary += chunk;
        basePay += ordinaryEquivalent;
        penaltyPay += chunkPay - ordinaryEquivalent;
      } else {
        bands.weekdayOrdinary += chunk;
        basePay += ordinaryEquivalent;
        penaltyPay += chunkPay - ordinaryEquivalent;
      }
    }

    if (isOutsideOrdinarySpan(hour)) outsideSpanHours += chunk;
    remaining -= chunk;
    cursor += chunk * 60 * 60 * 1000;
  }

  warnings.push(
    `SCHADS ${SCHADS_REMOTE_WORK_CLAUSE} remote work — ${kind.replaceAll('_', ' ')}; cl 10.5 minimum payments do not apply (cl 25.10(f)(ii)).`,
  );

  const grossTotal = sumMoney(basePay, penaltyPay, overtimePay);

  return {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: Math.round(payableHours * 10000) / 10000,
    bands: {
      weekdayOrdinary: Math.round(bands.weekdayOrdinary * 10000) / 10000,
      saturdayOrdinary: Math.round(bands.saturdayOrdinary * 10000) / 10000,
      sundayOrdinary: Math.round(bands.sundayOrdinary * 10000) / 10000,
      publicHoliday: Math.round(bands.publicHoliday * 10000) / 10000,
      eveningShift: 0,
      nightShift: 0,
    },

    basePay: roundMoney(basePay),

    penaltyPay: roundMoney(penaltyPay),
    shiftLoadingPay: 0,
    overtimeHours: Math.round(overtimeHours * 10000) / 10000,

    overtimePay: roundMoney(overtimePay),

    overtimeFirstHours: 0,

    overtimeRemainderHours: 0,

    overtimePublicHolidayHours: 0,
    allowancesPay: 0,
    allowanceLines: [],
    grossTotal,
    warnings,
  };
}

export function isSchadsRemoteWork(input: { isRemoteWork?: boolean } | null | undefined): boolean {
  return input?.isRemoteWork === true;
}

export function remoteWorkDoesNotCountForRest(
  prevIsRemote: boolean,
  currIsRemote: boolean,
): boolean {
  return prevIsRemote || currIsRemote;
}

export function remoteWorkDateKey(start: Date, timezone: string = DEFAULT_TZ): string {
  return dateOnlyInTz(start, timezone);
}
